/*
 * WR-024 B3: HorizonLanes — the "Lanes" popover to add, rename, recolour,
 * set capacity, bind a member, and delete lanes. Save hands the complete
 * list to the page.
 */

import React, { useState, useEffect } from 'react';
import './HorizonDialogs.css';
import { TAG_COLORS } from './tagPalette';

/**
 * Count how many entries reference this lane id.
 */
function countUsage(horizon, laneId) {
  if (!horizon || !Array.isArray(horizon.entries)) return 0;
  let count = 0;
  for (const entry of horizon.entries) {
    if (entry.lanes && entry.lanes.includes(laneId)) {
      count++;
    }
  }
  return count;
}

/**
 * Find the first palette color not used by any lane in the list.
 */
function findFirstUnusedColor(horizon) {
  const used = new Set();
  if (horizon && Array.isArray(horizon.lanes)) {
    for (const lane of horizon.lanes) {
      if (lane && lane.color) {
        used.add(lane.color);
      }
    }
  }
  for (const c of TAG_COLORS) {
    if (!used.has(c.id)) {
      return c.id;
    }
  }
  return TAG_COLORS[0].id; // fallback to slate
}

function HorizonLanes({ horizon, members, onSave, onClose }) {
  const [lanes, setLanes] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (horizon && Array.isArray(horizon.lanes)) {
      setLanes(JSON.parse(JSON.stringify(horizon.lanes)));
    }
  }, [horizon]);

  const handleNameChange = (index, value) => {
    const newLanes = [...lanes];
    newLanes[index].name = value;
    setLanes(newLanes);
  };

  const handleColorChange = (index, color) => {
    const newLanes = [...lanes];
    newLanes[index].color = color;
    setLanes(newLanes);
  };

  const handleCapacityChange = (index, value) => {
    const newLanes = [...lanes];
    newLanes[index].capacity = value;
    setLanes(newLanes);
  };

  const handleMemberChange = (index, value) => {
    const newLanes = [...lanes];
    newLanes[index].member = value === '' ? null : value;
    setLanes(newLanes);
  };

  const handleDelete = (index) => {
    const newLanes = lanes.filter((_, i) => i !== index);
    setLanes(newLanes);
  };

  const handleAdd = () => {
    const newLane = {
      name: '',
      color: findFirstUnusedColor({ lanes: lanes }),
      capacity: 'serial',
      member: null,
    };
    setLanes([...lanes, newLane]);
  };

  const handleSave = async () => {
    setError('');
    // Validate all names
    for (const lane of lanes) {
      if (!lane.name || !lane.name.trim()) {
        setError('Every lane needs a name.');
        return;
      }
    }

    try {
      const payload = lanes.map((lane) => {
        if (lane._id) {
          return { _id: lane._id, name: lane.name, color: lane.color, capacity: lane.capacity, member: lane.member };
        }
        return { name: lane.name, color: lane.color, capacity: lane.capacity, member: lane.member };
      });
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="hzl-body">
          <h2 className="hzl-title">Edit lanes</h2>
          {error && <div className="hzl-error">{error}</div>}
          <div className="hzl-lanes">
            {lanes.map((lane, index) => (
              <div key={lane._id || `new-${index}`} className="hzl-row">
                <input
                  className="hzl-name"
                  type="text"
                  value={lane.name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  placeholder="Lane name"
                />
                <div className="hzl-colors">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color.id}
                      className={`hzl-color hz-lane-${color.id} ${lane.color === color.id ? 'is-selected' : ''}`}
                      data-color={color.id}
                      onClick={() => handleColorChange(index, color.id)}
                      title={color.label}
                      type="button"
                    />
                  ))}
                </div>
                <select
                  className="hzl-capacity"
                  value={lane.capacity}
                  onChange={(e) => handleCapacityChange(index, e.target.value)}
                >
                  <option value="serial">Serial</option>
                  <option value="parallel">Parallel — never queues</option>
                </select>
                <select
                  className="hzl-member"
                  value={lane.member || ''}
                  onChange={(e) => handleMemberChange(index, e.target.value)}
                >
                  <option value="">No one</option>
                  {members &&
                    members.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                </select>
                <span className="hzl-used">
                  {(() => {
                    const count = countUsage(horizon, lane._id);
                    if (count === 0) return 'Unused';
                    return count === 1 ? '1 row' : `${count} rows`;
                  })()}
                </span>
                <button
                  className="hzl-delete"
                  onClick={() => handleDelete(index)}
                  disabled={countUsage(horizon, lane._id) > 0}
                  title={countUsage(horizon, lane._id) > 0 ? 'Rows use this lane — move them first' : 'Delete lane'}
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button className="hzl-add" onClick={handleAdd} type="button">
            Add lane
          </button>
        </div>
        <div className="hzl-actions">
          <button className="hzl-cancel btn btn-secondary" onClick={handleCancel} type="button">
            Cancel
          </button>
          <button className="hzl-save btn btn-primary" onClick={handleSave} type="button">
            Save lanes
          </button>
        </div>
      </div>
    </div>
  );
}

export default HorizonLanes;
